import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/common";
import { adminApi, errMsg, maskDoc, maskPhone, digits, SEGMENT_LABELS } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { razao_social: "", nome_fantasia: "", documento: "", responsavel: "", email: "", telefone: "", segmento: "alimentacao", login: "admin", password: "", trial_days: 15, send_email: true };

export function TenantFormDialog({ open, onOpenChange, tenant, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setF(tenant ? { ...EMPTY, ...tenant, documento: maskDoc(tenant.documento), telefone: maskPhone(tenant.telefone) } : EMPTY);
  }, [open, tenant]);
  const set = (k, mask) => (e) => setF((x) => ({ ...x, [k]: mask ? mask(e.target.value) : e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, documento: digits(f.documento), telefone: digits(f.telefone), trial_days: Number(f.trial_days) || 0 };
      if (tenant) await adminApi.put(`/tenants/${tenant.id}`, body);
      else {
        const { data } = await adminApi.post("/tenants", body);
        toast.success(`Cliente criado. Número de acesso: ${data.access_number}${data.email_sent ? " (enviado por e-mail)" : ""}`, { duration: 10000 });
      }
      if (tenant) toast.success("Cliente atualizado");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="tenant-form-dialog">
        <DialogHeader><DialogTitle>{tenant ? "Editar cliente" : "Novo cliente"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Razão social" className="sm:col-span-2"><Input value={f.razao_social} onChange={set("razao_social")} data-testid="tenant-razao-input" /></Field>
          <Field label="Nome fantasia"><Input value={f.nome_fantasia} onChange={set("nome_fantasia")} data-testid="tenant-fantasia-input" /></Field>
          <Field label="CPF ou CNPJ" hint={tenant ? "Para trocar, use \"Alterar documento\" nos detalhes do cliente" : ""}><Input value={f.documento} disabled={!!tenant} onChange={set("documento", maskDoc)} data-testid="tenant-doc-input" /></Field>
          <Field label="Responsável"><Input value={f.responsavel} onChange={set("responsavel")} data-testid="tenant-responsavel-input" /></Field>
          <Field label="E-mail"><Input type="email" value={f.email} onChange={set("email")} data-testid="tenant-email-input" /></Field>
          <Field label="Telefone"><Input value={f.telefone} onChange={set("telefone", maskPhone)} data-testid="tenant-phone-input" /></Field>
          <Field label="Segmento">
            <Select value={f.segmento} onValueChange={(v) => setF((x) => ({ ...x, segmento: v }))}>
              <SelectTrigger data-testid="tenant-segment-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(SEGMENT_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {!tenant && (<>
            <Field label="Login do proprietário"><Input value={f.login} onChange={set("login", (v) => v.toLowerCase().replace(/\s/g, ""))} data-testid="tenant-login-input" /></Field>
            <Field label="Senha inicial" hint="Mínimo 6 caracteres"><Input type="text" value={f.password} onChange={set("password")} data-testid="tenant-password-input" /></Field>
            <Field label="Dias de teste"><Input type="number" min={1} value={f.trial_days} onChange={set("trial_days")} data-testid="tenant-trial-days-input" /></Field>
            <label className="flex items-center gap-3 text-sm self-end pb-2"><Switch checked={f.send_email} onCheckedChange={(v) => setF((x) => ({ ...x, send_email: v }))} data-testid="tenant-send-email-switch" />Enviar número de acesso por e-mail</label>
          </>)}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={busy} data-testid="tenant-save-btn">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
